import { Button, Filter, InputSelect } from '@vrplatform/components';
import {
  type gqlV2,
  useInfiniteQuery,
  useMutation,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { InfiniteTable, type MRT_ColumnDef } from '@vrplatform/table';
import type { SelectItem } from '@vrplatform/components';
import { toTitleCase } from '@vrplatform/utils';
import { Avatar, Box, Group, Modal, Text, Tooltip } from '@mantine/core';
import { useSetState } from '@mantine/hooks';
import {
  getListingName,
  whereConnectionStatusDefault,
  whereListings,
} from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';
import { AddListingDrawer } from './AddListingDrawer';

type ListingMappingRow = {
  listingConnection: {
    id: string;
    name: string;
    connectionId: string;
    app: {
      name: string;
      icon: string;
    };
  };
  listing?: Omit<SelectItem, 'icon' | 'description'>;
};

function useListingMappingTableQuery(modalOpen: boolean, filter: FilterInput) {
  const [{ id: teamId }] = useTeam();

  return useInfiniteQuery(
    (q, { teamId, search, mapping }, { limit, offset }) => {
      const where: gqlV2.listing_connection_bool_exp = {
        tenantId: { _eq: teamId },
        connection: { status: whereConnectionStatusDefault },
        listingId: { _is_null: mapping === 'unmapped' },
        _or: search
          ? [
              {
                name: { _ilike: `%${search}%` },
              },
            ]
          : undefined,
      };

      const aggregate =
        q.listingConnectionAggregate({ where }).aggregate?.count() || 0;

      const list: ListingMappingRow[] = q
        .listingConnections({
          where,
          order_by: [{ name: 'asc' }],
          limit,
          offset,
        })
        .map<ListingMappingRow>((listingConnection) => {
          const hasListing = listingConnection.listing?.id;

          const listing = {
            label: getListingName(listingConnection.listing),
            value: listingConnection.listing?.id!,
          };

          return {
            listingConnection: {
              id: listingConnection.id,
              name: listingConnection.name || 'No name',
              connectionId: listingConnection.connection?.id,
              app: {
                name: listingConnection.connection?.name || 'No name',
                icon: listingConnection.connection?.app?.iconRound || '',
              },
            },
            listing: hasListing ? listing : undefined,
          };
        });

      return {
        list,
        aggregate,
      };
    },
    {
      variables: {
        teamId,
        modalOpen,
        search: filter.search?.trim(),
        mapping: filter.mapping,
      },
      skip: !teamId || !modalOpen,
      queryKey: ['listings', 'listingConnections'],
    }
  );
}

type FilterInput = {
  search: string;
  mapping: 'mapped' | 'unmapped';
};

export const ListingMappingModal = ({
  closeModal,
  opened,
  openModal,
}: {
  opened: boolean;
  closeModal: () => void;
  openModal: () => void;
}) => {
  const [openedDrawer, setOpenedDrawer] = useState(false);
  const [filter, setFilter] = useSetState<FilterInput>({
    search: '',
    mapping: 'unmapped',
  });
  const queryData = useListingMappingTableQuery(opened, filter);

  const columns = useMemo<MRT_ColumnDef<ListingMappingRow>[]>(
    () => [
      {
        accessorKey: 'listingConnection.name',
        header: 'Unit',
        Cell: ({ row, cell }) => {
          const data = row.original;

          return (
            <Group wrap="nowrap" sx={{ height: '100%' }}>
              <Tooltip
                label={data?.listingConnection.app.name || ''}
                withinPortal
                withArrow
              >
                <Box>
                  <Avatar size="sm" src={data?.listingConnection.app.icon} />
                </Box>
              </Tooltip>
              <Text component="span" lineClamp={1} sx={{ display: 'block' }}>
                {cell.getValue<string>()}
              </Text>
            </Group>
          );
        },
      },
      {
        header: 'arrow',
        Header: () => null,
        size: 50,
        maxSize: 0,
        minSize: 50,
        Cell: () => <Icon icon="ArrowRightIcon" size={18} />,
      },
      {
        accessorKey: 'listing.value',
        header: 'Listing',
        Cell: ({ row }) => (
          <Box w="100%" key={row.original.listingConnection.id}>
            <ListingSelectEditor data={row.original} />
          </Box>
        ),
      },
    ],
    []
  );

  const openAddListingDrawer = () => {
    setOpenedDrawer(true);
    closeModal();
  };

  const closeAddListingDrawer = () => {
    setOpenedDrawer(false);
    openModal();
  };

  return (
    <>
      <AddListingDrawer
        opened={openedDrawer}
        closeDrawer={closeAddListingDrawer}
      />
      <Modal
        opened={opened}
        onClose={closeModal}
        size={900}
        closeOnEscape={false}
        centered
        styles={{
          body: {
            paddingTop: 2,
            paddingBottom: 0,
          },
          header: {
            display: 'none',
          },
        }}
        overlayProps={{
          opacity: 0.3,
        }}
      >
        <Group
          title={'Link Units to Listings'}
          my="sm"
          sx={{
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
          }}
        >
          <Group justify="space-between" w="100%">
            <Text fw={500} size="md">
              Link Units to Listings
            </Text>
            <Button
              size="xs"
              leftIcon={'PlusIcon'}
              onClick={openAddListingDrawer}
            >
              Add listing
            </Button>
          </Group>
          <Modal.CloseButton />
        </Group>
        <Box
          sx={() => ({
            display: 'flex',
            flexDirection: 'column',
            height: '50vh',
            minHeight: 500,
            maxHeight: 800,
          })}
        >
          <InfiniteTable
            queryData={queryData}
            columns={columns}
            resetFilter={() => null}
          >
            <Group gap="xs" w="100%">
              <Filter.Search
                value={filter.search}
                setValue={(s) =>
                  setFilter({
                    search: s,
                  })
                }
              />

              <Filter.Select
                type="single"
                value={
                  filter.mapping
                    ? {
                        label: toTitleCase(filter.mapping),
                        value: filter.mapping,
                      }
                    : null
                }
                setValue={(v) =>
                  v?.value &&
                  setFilter({
                    mapping: v.value,
                  })
                }
                data={{
                  options: [
                    {
                      label: 'Mapped',
                      value: 'mapped',
                    },
                    {
                      label: 'Unmapped',
                      value: 'unmapped',
                    },
                  ],
                }}
                label="Mapping"
                hideClearButton
              />
              <Button
                variant="light"
                leftIcon={'RefreshCwIcon'}
                onClick={() => queryData.refetch()}
                sx={{
                  marginLeft: 'auto',
                }}
              >
                Refresh data
              </Button>
            </Group>
          </InfiniteTable>
        </Box>
      </Modal>
    </>
  );
};

const ListingSelectEditor = ({ data }: { data: ListingMappingRow }) => {
  const [{ isGL: GL, id: teamId }] = useTeam();
  const [search, setSearch] = useState('');

  const [newValue, setNewValue] = useState<SelectItem | null | undefined>(
    undefined
  );

  const [error, setError] = useState<string | undefined>(undefined);

  const { mutate, loading: loadingMutation } = useMutation(
    (
      q,
      args: {
        listing: SelectItem | null;
        listingConnectionId: string;
      }
    ) => {
      const res = q.updateListingConnection({
        pk_columns: {
          id: args.listingConnectionId,
        },
        _set: {
          listingId: args.listing?.value || null,
        },
      })?.id;

      if (!res) return undefined;

      return args.listing;
    },
    {
      successMessage: {
        title: 'Success!',
        message: 'Successfully mapped listing connections.',
      },
      errorMessage: {
        message: 'Failed to map listing connections.',
      },
      // invalidateQueryKeys: ['listings'],
    }
  );

  const handleChange = async (value: SelectItem | null) =>
    await mutate({
      args: {
        listing: value,
        listingConnectionId: data.listingConnection.id,
      },
    })
      .then(setNewValue)
      .catch((e) => setError(e.message));

  const value = newValue || data.listing || null;

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: gqlV2.listing_bool_exp = whereListings({
        tenantId: args.teamId,
        dashboard: 'propertyManager',
        search: args.search,
        ownerPortalContactId: null,
        GL: args.GL,
      });

      const list = q
        .listings({
          where,
          limit,
          offset,
          order_by: [{ calculated_title: 'asc_nulls_last' }],
        })
        .map<SelectItem>((listing) => ({
          label: getListingName(listing),
          value: listing.id,
        }));

      const aggregate = q.listingAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      queryKey: ['listings'],
      variables: {
        teamId,
        search: search?.trim(),
        GL,
      },
      skip: !teamId,
    }
  );

  return (
    <InputSelect
      type="single"
      value={value}
      setValue={handleChange}
      infiniteData={{
        ...queryData,
        setSearch,
      }}
      dropdownProps={{
        withinPortal: true,
        width: 'target',
      }}
      inputProps={{
        placeholder: 'Select listing',
        loadingMutation,
        error,
      }}
    />
  );
};
